// apps/patient/src/components/programs/ExerciseNowModal.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Flame,
  Heart,
  Zap,
  Wind,
  StretchHorizontal,
  Activity,
  Timer,
  MapPin,
  Check,
  Loader2,
  Target,
  TrendingUp,
  Scale,
  Sparkles
} from 'lucide-react';
import { PatientLocation } from '@prism-ehr/shared';
import { patientApiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import { LOCATION_TYPE_ICONS } from '../../constants/equipment';

interface ExerciseNowModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Track definitions with icons and descriptions
const TRACKS = [
  {
    id: 'general_fitness',
    name: 'General Fitness',
    description: 'Balanced workout for overall health',
    icon: Activity,
    color: 'bg-blue-500'
  },
  {
    id: 'weight_loss',
    name: 'Weight Loss',
    description: 'High-energy fat burning workout',
    icon: Scale,
    color: 'bg-orange-500'
  },
  {
    id: 'muscle_building',
    name: 'Muscle Building',
    description: 'Build strength and muscle mass',
    icon: Dumbbell,
    color: 'bg-purple-500'
  },
  {
    id: 'strength',
    name: 'Strength',
    description: 'Focus on power and lifting',
    icon: TrendingUp,
    color: 'bg-red-500'
  },
  {
    id: 'cardio',
    name: 'Cardio',
    description: 'Improve cardiovascular endurance',
    icon: Heart,
    color: 'bg-pink-500'
  },
  {
    id: 'flexibility',
    name: 'Flexibility & Balance',
    description: 'Stretching, yoga, and mobility',
    icon: StretchHorizontal,
    color: 'bg-teal-500'
  },
  {
    id: 'hiit',
    name: 'HIIT',
    description: 'High-intensity interval training',
    icon: Zap,
    color: 'bg-yellow-500'
  }
];

// Workout types based on selected track
const WORKOUT_TYPES: Record<string, { id: string; name: string; description: string }[]> = {
  general_fitness: [
    { id: 'full_body', name: 'Full Body', description: 'Complete workout targeting all muscle groups' },
    { id: 'upper_body', name: 'Upper Body', description: 'Focus on chest, back, shoulders, and arms' },
    { id: 'lower_body', name: 'Lower Body', description: 'Target legs, glutes, and core' },
    { id: 'core', name: 'Core Focus', description: 'Strengthen your midsection' }
  ],
  weight_loss: [
    { id: 'full_body_hiit', name: 'Full Body HIIT', description: 'Maximum calorie burn with intervals' },
    { id: 'cardio_strength', name: 'Cardio + Strength', description: 'Combined fat burning approach' },
    { id: 'metabolic', name: 'Metabolic Conditioning', description: 'Boost your metabolism' },
    { id: 'circuit', name: 'Circuit Training', description: 'Fast-paced station workout' }
  ],
  muscle_building: [
    { id: 'push', name: 'Push Day', description: 'Chest, shoulders, and triceps' },
    { id: 'pull', name: 'Pull Day', description: 'Back and biceps focus' },
    { id: 'legs', name: 'Leg Day', description: 'Quads, hamstrings, and glutes' },
    { id: 'upper', name: 'Upper Body', description: 'Complete upper body session' },
    { id: 'full_body', name: 'Full Body', description: 'Hit all major muscle groups' }
  ],
  strength: [
    { id: 'squat_focus', name: 'Squat Focus', description: 'Lower body strength emphasis' },
    { id: 'press_focus', name: 'Press Focus', description: 'Pushing strength workout' },
    { id: 'pull_focus', name: 'Pull Focus', description: 'Pulling strength workout' },
    { id: 'full_body_strength', name: 'Full Body Strength', description: 'Total body strength session' }
  ],
  cardio: [
    { id: 'steady_state', name: 'Steady State', description: 'Moderate pace sustained cardio' },
    { id: 'intervals', name: 'Interval Training', description: 'Alternating intensity periods' },
    { id: 'mixed_cardio', name: 'Mixed Cardio', description: 'Variety of cardio exercises' },
    { id: 'low_impact', name: 'Low Impact', description: 'Joint-friendly cardio options' }
  ],
  flexibility: [
    { id: 'yoga_flow', name: 'Yoga Flow', description: 'Dynamic yoga sequences' },
    { id: 'mobility', name: 'Mobility Work', description: 'Joint mobility and movement' },
    { id: 'stretching', name: 'Stretching', description: 'Full body stretch routine' },
    { id: 'recovery', name: 'Active Recovery', description: 'Gentle movement for rest days' }
  ],
  hiit: [
    { id: 'tabata', name: 'Tabata', description: '20 seconds work, 10 seconds rest' },
    { id: 'emom', name: 'EMOM', description: 'Every minute on the minute' },
    { id: 'amrap', name: 'AMRAP', description: 'As many rounds as possible' },
    { id: 'classic_hiit', name: 'Classic HIIT', description: 'Traditional interval format' }
  ]
};

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 15, label: '15 min', description: 'Quick session' },
  { value: 20, label: '20 min', description: 'Short workout' },
  { value: 30, label: '30 min', description: 'Standard' },
  { value: 45, label: '45 min', description: 'Extended' },
  { value: 60, label: '60 min', description: 'Full session' },
  { value: 90, label: '90 min', description: 'Long session' }
];

type Step = 'track' | 'workout_type' | 'location' | 'duration' | 'generating';

export function ExerciseNowModal({ isOpen, onClose }: ExerciseNowModalProps) {
  const navigate = useNavigate();
  const { patient } = usePatient();

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('track');

  // Selection state
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);
  const [selectedWorkoutType, setSelectedWorkoutType] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<PatientLocation | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(30);

  // Location data
  const [locations, setLocations] = useState<PatientLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('track');
      setSelectedTrack(null);
      setSelectedWorkoutType(null);
      setSelectedLocation(null);
      setSelectedDuration(30);
      setError(null);
      loadLocations();
    }
  }, [isOpen]);

  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      const data = await patientApiService.getMyLocations();
      setLocations(data || []);

      // Auto-select default location if available
      const defaultLoc = data?.find((l: PatientLocation) => l.is_default);
      if (defaultLoc) {
        setSelectedLocation(defaultLoc);
      }
    } catch (err) {
      console.error('Failed to load locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleTrackSelect = (trackId: string) => {
    setSelectedTrack(trackId);
    setSelectedWorkoutType(null); // Reset workout type when track changes
    setCurrentStep('workout_type');
  };

  const handleWorkoutTypeSelect = (typeId: string) => {
    setSelectedWorkoutType(typeId);
    setCurrentStep('location');
  };

  const handleLocationSelect = (location: PatientLocation) => {
    setSelectedLocation(location);
  };

  const handleLocationConfirm = () => {
    if (selectedLocation) {
      setCurrentStep('duration');
    }
  };

  const handleDurationSelect = (duration: number) => {
    setSelectedDuration(duration);
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'workout_type':
        setCurrentStep('track');
        break;
      case 'location':
        setCurrentStep('workout_type');
        break;
      case 'duration':
        setCurrentStep('location');
        break;
    }
  };

  const handleGenerate = async () => {
    if (!selectedTrack || !selectedWorkoutType || !selectedLocation) {
      setError('Please complete all selections');
      return;
    }

    try {
      setGenerating(true);
      setCurrentStep('generating');
      setError(null);

      // Get fitness level from patient profile
      const fitnessLevel = patient?.patient?.fitness_profile?.fitness_level || 'intermediate';

      const result = await patientApiService.generateExerciseNowWorkout({
        track: selectedTrack,
        workout_type: selectedWorkoutType,
        location_id: selectedLocation.id,
        equipment: selectedLocation.equipment,
        duration_minutes: selectedDuration,
        fitness_level: fitnessLevel
      });

      if (result.success && result.assignment_id) {
        // Navigate to session player with the generated workout
        // Pass location_id to skip location selection (already selected in this modal)
        const today = new Date().toISOString().split('T')[0];
        onClose();
        navigate(`/programs/${result.assignment_id}/session/${today}?locationId=${selectedLocation.id}`);
      } else {
        throw new Error(result.message || 'Failed to generate workout');
      }
    } catch (err: any) {
      console.error('Failed to generate workout:', err);
      setError(err.message || 'Failed to generate workout. Please try again.');
      setCurrentStep('duration'); // Go back to duration step on error
    } finally {
      setGenerating(false);
    }
  };

  const navigateToAddLocation = () => {
    onClose();
    navigate('/settings/locations?returnTo=/dashboard');
  };

  if (!isOpen) return null;

  const selectedTrackData = TRACKS.find(t => t.id === selectedTrack);
  const workoutTypes = selectedTrack ? WORKOUT_TYPES[selectedTrack] || [] : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-t-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0">
          <div className="flex items-center gap-3">
            {currentStep !== 'track' && currentStep !== 'generating' && (
              <button
                onClick={handleBack}
                className="p-1.5 -ml-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Exercise Now</h2>
              <p className="text-sm text-gray-500">
                {currentStep === 'track' && 'Choose your focus'}
                {currentStep === 'workout_type' && 'Select workout type'}
                {currentStep === 'location' && 'Where are you working out?'}
                {currentStep === 'duration' && 'How much time do you have?'}
                {currentStep === 'generating' && 'Creating your workout...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={generating}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Indicator */}
        {currentStep !== 'generating' && (
          <div className="px-4 py-2 bg-gray-50 border-b">
            <div className="flex items-center gap-2">
              {['track', 'workout_type', 'location', 'duration'].map((step, index) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-2 h-2 rounded-full transition-colors ${
                      currentStep === step
                        ? 'bg-primary'
                        : ['track', 'workout_type', 'location', 'duration'].indexOf(currentStep) > index
                        ? 'bg-primary/60'
                        : 'bg-gray-300'
                    }`}
                  />
                  {index < 3 && (
                    <div
                      className={`w-8 sm:w-12 h-0.5 mx-1 transition-colors ${
                        ['track', 'workout_type', 'location', 'duration'].indexOf(currentStep) > index
                          ? 'bg-primary/60'
                          : 'bg-gray-300'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Track Selection */}
          {currentStep === 'track' && (
            <div className="grid grid-cols-1 gap-3">
              {TRACKS.map((track) => {
                const Icon = track.icon;
                return (
                  <button
                    key={track.id}
                    onClick={() => handleTrackSelect(track.id)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                      selectedTrack === track.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`p-3 rounded-xl ${track.color} text-white`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{track.name}</h3>
                      <p className="text-sm text-gray-500">{track.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Workout Type Selection */}
          {currentStep === 'workout_type' && (
            <div className="space-y-4">
              {selectedTrackData && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${selectedTrackData.color} text-white`}>
                    <selectedTrackData.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{selectedTrackData.name}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                {workoutTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleWorkoutTypeSelect(type.id)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 text-left transition-all ${
                      selectedWorkoutType === type.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">{type.name}</h3>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Location Selection */}
          {currentStep === 'location' && (
            <div className="space-y-4">
              {loadingLocations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">No locations yet</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Add a workout location to get equipment-optimized exercises
                  </p>
                  <button
                    onClick={navigateToAddLocation}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Add Location
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3">
                    {locations.map((location) => (
                      <button
                        key={location.id}
                        onClick={() => handleLocationSelect(location)}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                          selectedLocation?.id === location.id
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-2xl">
                          {LOCATION_TYPE_ICONS[location.location_type] || '📍'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 truncate">{location.name}</h3>
                            {location.is_default && (
                              <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded flex-shrink-0">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {location.equipment.length} equipment items
                          </p>
                        </div>
                        {selectedLocation?.id === location.id && (
                          <Check className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={navigateToAddLocation}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-all"
                  >
                    + Add New Location
                  </button>
                </>
              )}
            </div>
          )}

          {/* Duration Selection */}
          {currentStep === 'duration' && (
            <div className="space-y-4">
              {/* Summary of selections */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Target className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">Track:</span>
                  <span className="font-medium text-gray-900">{selectedTrackData?.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Dumbbell className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">Workout:</span>
                  <span className="font-medium text-gray-900">
                    {workoutTypes.find(t => t.id === selectedWorkoutType)?.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium text-gray-900">{selectedLocation?.name}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleDurationSelect(option.value)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      selectedDuration === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Timer className="h-4 w-4 text-gray-500" />
                      <span className="font-semibold text-gray-900">{option.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Generating State */}
          {currentStep === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-10 w-10 text-primary" />
                </div>
                <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Your Workout</h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                Building a personalized {selectedDuration}-minute {selectedTrackData?.name.toLowerCase()} workout...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {currentStep !== 'generating' && (
          <div className="p-4 border-t bg-white sticky bottom-0">
            {currentStep === 'location' && locations.length > 0 && (
              <button
                onClick={handleLocationConfirm}
                disabled={!selectedLocation}
                className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}

            {currentStep === 'duration' && (
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Generate Workout
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
